import { EventModelConfig } from '@bryntum/schedulerpro';
import { Dictionary } from 'lodash';
import { Holiday as HolidayType } from 'api/holiday-calendar/types';
import {
  AllocationMedium,
  AllocationSourceTask,
  OverAllocationApi,
} from 'api/resource-management/types';
import { TimeoffResponse } from 'api/timeoff/types';
import { Minutes } from '../user-capacity';
import { ViewPresetId } from './scheduler-config/view-presets/constants';
import { AllocatedMinutes } from './types';

export enum SchedulerEventType {
  UserAllocationSummary = 'user_allocation_summary',
  TeamAllocationSummary = 'team_allocation_summary',
  CompanyAllocationSummary = 'company_allocation_summary',
  Allocation = 'allocation',
  ProjectTimeline = 'project_timeline',
  ProjectAsALeafTimeline = 'project_as_a_leaf_timeline',
  ProjectPhase = 'project_phase',
  DemandRole = 'demand_role',
  DemandCapacity = 'demand_capacity',
  TotalAllocation = 'total_allocation',
  UserTotalAllocation = 'user_total_allocation',
  UserTotalSoftAllocation = 'user_total_soft_allocation',
  UserTotalHardAllocation = 'user_total_hard_allocation',
  TeamTotalAllocation = 'team_total_allocation',
  TeamTotalSoftAllocation = 'team_total_soft_allocation',
  TeamTotalHardAllocation = 'team_total_hard_allocation',
  UserTimeOff = 'user_time_off',
  UserFte = 'user_fte',
  PartnerFte = 'partner_fte',
  TimeOffFte = 'timeoff_fte',
  HolidayFte = 'holiday_fte',
  CapacityAllocationDiffFte = 'capacity_allocation_diff_fte',
  ProjectsAllocationInRoleFte = 'projects_allocation_fte',
  Holiday = 'holiday',
}
export type EventModelConfigWithoutMandatoryFields = Omit<
  EventModelConfig,
  'id' | 'resourceId' | 'startDate' | 'endDate'
>;

export interface OverAllocationForUi {
  startDate: Date;
  endDate: Date;
  overAllocatedMinutes: number;
}

interface BaseEvent extends Partial<EventModelConfigWithoutMandatoryFields> {
  id: string | number;
  resourceId: string;
  startDate: Date;
  endDate: Date;
}
export interface BaseAllocationEvent extends BaseEvent {
  type: SchedulerEventType.Allocation;
  minutes: number;
  totalMinutes: number;
  projectPhase?: {
    id: number;
    name: string;
  };
  billable: boolean;
  overAllocations: OverAllocationApi[];
  overAllocationsForUI: OverAllocationForUi[];
  projectId: number;
  medium: AllocationMedium;
  sources?: AllocationSourceTask[];
}
export interface UserAllocationEvent extends BaseAllocationEvent {
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  rbacType?: string;
}
export interface TeamAllocationEvent extends BaseAllocationEvent {
  teamId: number;
  roleId: number;
  rbacType?: string;
}
export type AllocationEvent = UserAllocationEvent | TeamAllocationEvent;

export interface UserAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.UserAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  capacityMinutes: Minutes;
  forViewPreset: ViewPresetId;
  timeOff?: TimeoffResponse[];
}

export interface TeamAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.TeamAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}
export interface CompanyAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.CompanyAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}

export type AllocationSummaryEvent =
  | UserAllocationSummaryEvent
  | TeamAllocationSummaryEvent
  | CompanyAllocationSummaryEvent;

export interface ProjectTimelineEvent extends BaseEvent {
  type: SchedulerEventType.ProjectTimeline;
  showInDetail?: boolean;
}
export interface ProjectAsLeafTimelineEvent extends BaseEvent {
  type: SchedulerEventType.ProjectAsALeafTimeline;
}

export interface TimeOffEvent extends BaseEvent {
  type: SchedulerEventType.UserTimeOff;
  timeOff: TimeoffResponse;
  userId: number;
}

export interface HolidayEvent extends BaseEvent {
  type: SchedulerEventType.Holiday;
  holiday: HolidayType;
  userId: number;
}

export interface ProjectPhaseEvent extends BaseEvent {
  type: SchedulerEventType.ProjectPhase;
  phaseName: string;
  groupKey: string;
  rowIndex: number;
  activePhase: boolean;
  top: number;
  height: number;
  showInDetail?: boolean;
}
type BaseDemandSummaryEvent = BaseEvent & {
  roleId: number;
  capacityMinutes: Minutes;
  totalAllocationFte: number;
  forViewPreset: ViewPresetId;
};
export interface DemandRoleEvent extends BaseDemandSummaryEvent {
  type: SchedulerEventType.DemandRole;
}
export interface DemandCapacityEvent extends BaseEvent {
  type: SchedulerEventType.DemandCapacity;
  forViewPreset: ViewPresetId;
  roleId: number;
  capacityMinutes: Minutes;
}
type BaseDemandCapacityDetailsEvent = BaseEvent & {
  roleId: number;
  forViewPreset: ViewPresetId;
};
export interface DemandCapacityTeamMemberFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.UserFte;
  teamMemberCapacityFte: number;
}
export interface DemandCapacityPartnerFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.PartnerFte;
  partnerCapacityFte: number;
}
export interface DemandCapacityTimeOffFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.TimeOffFte;
  timeOffCapacityFte: number;
}
export interface DemandCapacityHolidayFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.HolidayFte;
  holidayCapacityFte: number;
}
export interface TotalAllocationEvent extends BaseDemandSummaryEvent {
  type: SchedulerEventType.TotalAllocation;
}

export interface DemandFteDiffEvent extends BaseEvent {
  type: SchedulerEventType.CapacityAllocationDiffFte;
  forViewPreset: ViewPresetId;
  roleId: number;
  capacityAllocationDiffFte: number;
}

export interface ProjectsAllocationInRoleFteEvent extends BaseEvent {
  type: SchedulerEventType.ProjectsAllocationInRoleFte;
  forViewPreset: ViewPresetId;
  roleId: number;
  allocationInProjectFte: number;
  projectId: number;
}

export type ResourceManagementEvents =
  | AllocationEvent
  | UserAllocationSummaryEvent
  | TeamAllocationSummaryEvent
  | CompanyAllocationSummaryEvent
  | ProjectTimelineEvent
  | ProjectAsLeafTimelineEvent
  | ProjectPhaseEvent
  | TimeOffEvent
  | HolidayEvent;

export type DemandEvents =
  | DemandRoleEvent
  | DemandCapacityEvent
  | TotalAllocationEvent
  | DemandCapacityTeamMemberFteEvent
  | DemandCapacityPartnerFteEvent
  | DemandCapacityTimeOffFteEvent
  | DemandCapacityHolidayFteEvent
  | DemandFteDiffEvent
  | ProjectsAllocationInRoleFteEvent;

export interface RecourceSummaries {
  userSummaries: Dictionary<UserAllocationSummaryEvent[]>;
  teamSummaries: Dictionary<TeamAllocationSummaryEvent[]>;
  companySummaries: Dictionary<CompanyAllocationSummaryEvent[]>;
}

export enum AllocationFrom {
  People = 'People',
  Project = 'Projects',
}
